import { HttpErrorResponse } from '@angular/common/http';

import { createAction, props } from '@ngrx/store';

import { Screen } from '@core/interfaces';

import { ErrorResponse } from '#types/core';
import { Filters, Sorts } from '#types/grid';
import { MultipleListOfValuesResponse } from '#types/list';
import {
  OrderAddItemResponseBody,
  OrderCreateRequest,
  OrderCreateResponseBody,
  OrderDetailUpdateItemRequest,
  OrderDetailUpdateItemResponseBody,
  OrderInquiryAttachmentResponseBody,
  OrderInquiryCancelOrderResponseBody,
  OrderInquiryDetailResponseBody,
  OrderInquiryDownloadAttachmentResponseBody,
  OrderInquiryLockOrderResponseBody,
  OrderInquiryResponseBody,
  OrderInquiryResponseRow,
  OrderInquiryShippingDetailResponseBody,
  OrderInquiryUpdateResponseBody,
} from '#types/order';

//
// order / request lists
//

export const RequestLists = createAction('[Order] - Request Lists');

//
// order lists
//

export const RequestOrderLists = createAction('[Order.Lists] - Request Order Lists');
export const RequestOrderListsSuccess = createAction(
  '[Order.Lists] - Request Order Lists Success',
  props<{ lists: MultipleListOfValuesResponse }>()
);
export const RequestOrderListsFailure = createAction('[Order.Lists] - Request Order Lists Failure', props<{ error: ErrorResponse }>());

//
// order / inquiry
//

export const InquiryInitialize = createAction('[Order.Inquiry] - Initialize', props<{ screen: Screen }>());

export const InquiryRequestData = createAction('[Order.Inquiry] - Grid Request Data', props<{ id: string }>());
export const InquiryRequestDataWithParameters = createAction(
  '[Order.Inquiry] - Grid Request Data With Parameters',
  props<{ id: string; filters: Filters; page: number; first: number; rows: number; sorts: Sorts }>()
);
export const InquiryRequestDataWithFilters = createAction(
  '[Order.Inquiry] - Grid Request Data With Filters',
  props<{ id: string; filters: Filters }>()
);
export const InquiryRequestDataForExport = createAction(
  '[Order.Inquiry] - Request Data For Export',
  props<{ id: string; format: string; rows: number }>()
);
export const InquiryRequestDataSuccess = createAction(
  '[Order.Inquiry] - Grid Request Data Success',
  props<{ id: string; data: OrderInquiryResponseBody }>()
);
export const InquiryRequestDataFailure = createAction('[Order.Inquiry] - Grid Request Data Failure', props<{ id: string; data: null }>());

//
// order / inquiry / update
//

export const OrderInquiryUpdateRequestData = createAction(
  '[Order.Inquiry] - Update Order Request',
  props<{ payload: OrderInquiryResponseRow }>()
);

export const OrderInquiryUpdateRequestDataSuccess = createAction(
  '[Order.Inquiry] - Update Order Request Success',
  props<{ payload: OrderInquiryUpdateResponseBody }>()
);
export const OrderInquiryUpdateRequestDataFailure = createAction(
  '[Order.Inquiry] - Update Order Request Failure',
  props<{ error: ErrorResponse }>()
);

//
// order / inquiry / shipping detail
//

export const InquiryShippingDetailRequestData = createAction(
  '[Order.Inquiry.Shipping-Detail] - Grid Request Data',
  props<{ id: string }>()
);
export const InquiryShippingDetailRequestDataWithParameters = createAction(
  '[Order.Inquiry.Shipping-Detail] - Grid Request Data With Parameters',
  props<{ id: string; filters: Filters; page: number; first: number; rows: number; sorts: Sorts }>()
);
export const InquiryShippingDetailRequestDataWithFilters = createAction(
  '[Order.Inquiry.Shipping-Detail] - Grid Request Data With Filters',
  props<{ id: string; filters: Filters }>()
);
export const InquiryShippingDetailRequestDataSuccess = createAction(
  '[Order.Inquiry.Shipping-Detail] - Grid Request Data Success',
  props<{ id: string; data: OrderInquiryShippingDetailResponseBody }>()
);
export const InquiryShippingDetailRequestDataFailure = createAction(
  '[Order.Inquiry.Shipping-Detail] - Grid Request Data Failure',
  props<{ id: string; data: null }>()
);

//
// order / inquiry / attachment
//

export const InquiryAttachmentRequestData = createAction('[Order.Inquiry.Attachment] - Grid Request Data', props<{ id: string }>());
export const InquiryAttachmentRequestDataWithParameters = createAction(
  '[Order.Inquiry.Attachment] - Grid Request Data With Parameters',
  props<{ id: string; filters: Filters; page: number; first: number; rows: number; sorts: Sorts }>()
);
export const InquiryAttachmentRequestDataWithFilters = createAction(
  '[Order.Inquiry.Attachment] - Grid Request Data With Filters',
  props<{ id: string; filters: Filters }>()
);
export const InquiryAttachmentRequestDataSuccess = createAction(
  '[Order.Inquiry.Attachment] - Grid Request Data Success',
  props<{ id: string; data: OrderInquiryAttachmentResponseBody }>()
);
export const InquiryAttachmentRequestDataFailure = createAction(
  '[Order.Inquiry.Attachment] - Grid Request Data Failure',
  props<{ id: string; data: null }>()
);

//
// order / inquiry / download attachment
//

export const InquiryAttachmentRequestDownload = createAction(
  '[Order.Inquiry] - Download Attachment Request',
  props<{ filePath: string }>()
);
export const InquiryAttachmentRequestDownloadSuccess = createAction(
  '[Order.Inquiry] - Download Attachment Request Success',
  props<{ filePath: string; data: OrderInquiryDownloadAttachmentResponseBody }>()
);
export const InquiryAttachmentRequestDownloadFailure = createAction(
  '[Order.Inquiry] - Download Attachment Request Failure',
  props<{ error: HttpErrorResponse }>()
);

//
// order / inquiry / cancel order
//

export const OrderInquiryCancelOrderRequest = createAction(
  '[Order.Inquiry] - Cancel Order Request',
  props<{ orderid: number; shipid: number; fromDetailScreen: boolean }>()
);
export const OrderInquiryCancelOrderRequestSuccess = createAction(
  '[Order.Inquiry] - Cancel Order Request Success',
  props<{ orderid: number; shipid: number; fromDetailScreen: boolean; data: OrderInquiryCancelOrderResponseBody }>()
);
export const OrderInquiryCancelOrderRequestFailure = createAction(
  '[Order.Inquiry] - Cancel Order Request Failure',
  props<{ error: HttpErrorResponse }>()
);

//
// order / inquiry / lock order
//

export const OrderInquiryLockOrderRequest = createAction(
  '[Order.Inquiry] - Lock Order Request',
  props<{ orderid: number; shipid: number; fromDetailScreen: boolean }>()
);
export const OrderInquiryLockOrderRequestSuccess = createAction(
  '[Order.Inquiry] - Lock Order Request Success',
  props<{ orderid: number; shipid: number; fromDetailScreen: boolean; data: OrderInquiryLockOrderResponseBody }>()
);
export const OrderInquiryLockOrderRequestFailure = createAction(
  '[Order.Inquiry] - Lock Order Request Failure',
  props<{ error: HttpErrorResponse }>()
);

//
// order / create-order
//

export const CreateOrderInitialize = createAction('[Order.Create-Order] - Initialize', props<{ screen: Screen }>());

export const CreateOrderClearData = createAction('[Order.Create-Order] - Clear Data');

//
// order / detail
//

export const InquiryDetailInitialize = createAction('[Order.Inquiry.Detail] - Initialize', props<{ screen: Screen }>());

export const InquiryDetailSetData = createAction('[Order.Inquiry.Detail] - Set Data', props<{ data: OrderInquiryResponseRow }>());
export const InquiryDetailClearData = createAction('[Order.Inquiry.Detail] - Clear Data');

export const InquiryDetailRequestData = createAction('[Order.Inquiry.Detail] - Grid Request Data', props<{ id: string }>());
export const InquiryDetailRequestDataWithParameters = createAction(
  '[Order.Inquiry.Detail] - Grid Request Data With Parameters',
  props<{ id: string; filters: Filters; page: number; first: number; rows: number; sorts: Sorts }>()
);
export const InquiryDetailRequestDataWithFilters = createAction(
  '[Order.Inquiry.Detail] - Grid Request Data With Filters',
  props<{ id: string; filters: Filters }>()
);
export const InquiryDetailRequestDataForExport = createAction(
  '[Order.Inquiry.Detail] - Request Data For Export',
  props<{ id: string; format: string; rows: number }>()
);
export const InquiryDetailRequestDataSuccess = createAction(
  '[Order.Inquiry.Detail] - Grid Request Data Success',
  props<{ id: string; data: OrderInquiryDetailResponseBody }>()
);
export const InquiryDetailRequestDataFailure = createAction(
  '[Order.Inquiry.Detail] - Grid Request Data Failure',
  props<{ id: string; data: null }>()
);

//
// order/ detail / update item
//

export const UpdateItemDialogOpen = createAction('[Order.Update-Item-Dialog] - Open');

export const UpdateItemDialogClose = createAction('[Order.Update-Item-Dialog] - Close');

export const UpdateItemRequest = createAction(
  '[Order.Update-Item-Dialog] - Set Item Detail',
  props<{
    payload: OrderDetailUpdateItemRequest;
  }>()
);

export const UpdateItemRequestSuccess = createAction(
  '[Order.Update-Item-Dialog] - Success',
  props<{ payload: OrderDetailUpdateItemRequest; data: OrderDetailUpdateItemResponseBody }>()
);
export const UpdateItemRequestFailure = createAction('[Order.Update-Item-Dialog] - Failure', props<{ error: HttpErrorResponse }>());

//
// order / detail / add item
//

export const AddItemDialogOpen = createAction('[Order.Add-Item-Dialog] - Open');

export const AddItemDialogClose = createAction('[Order.Add-Item-Dialog] - Close');

export const AddItemDialogSetStep = createAction('[Order.Add-Item-Dialog] - Set Step', props<{ step: number }>());

export const AddItemDialogSetItem = createAction('[Order.Add-Item-Dialog] - Set Item', props<{ item: string; description: string }>());

export const AddItemDialogSetItemDetail = createAction(
  '[Order.Add-Item-Dialog] - Set Item Detail',
  props<{
    lotNumber: string;
    uom: string;
    weightOrUom: string;
    quantity: number;
    weight: number;
    weightUom: string;
  }>()
);

export const AddItemDialogClearItem = createAction('[Order.Add-Item-Dialog] - Clear Item');

export const AddItemDialogStep0ToStep1 = createAction(
  '[Order.Add-Item-Dialog] - Step 0 to Step 1',
  props<{ item: string; description: string }>()
);

export const AddItemDialogStep1ToStep2 = createAction(
  '[Order.Add-Item-Dialog] - Step 1 to Step 2',
  props<{
    lotNumber: string;
    uom: string;
    weightOrUom: string;
    quantity: number;
    weight: number;
    weightUom: string;
  }>()
);

export const AddItemDialogStep1ToComplete = createAction(
  '[Order.Add-Item-Dialog] - Step 1 to Complete',
  props<{
    lotNumber: string;
    uom: string;
    weightOrUom: string;
    quantity: number;
    weight: number;
    weightUom: string;
  }>()
);

export const AddItemDialogStep2ToComplete = createAction(
  '[Order.Add-Item-Dialog] - Step 2 to Complete',
  props<{
    dtlpassthruchar01: string;
    dtlpassthruchar02: string;
    dtlpassthruchar03: string;
    dtlpassthruchar04: string;
    dtlpassthruchar05: string;
    dtlpassthruchar06: string;
    dtlpassthruchar07: string;
    dtlpassthruchar08: string;
    dtlpassthruchar09: string;
    dtlpassthruchar10: string;
    dtlpassthruchar11: string;
    dtlpassthruchar12: string;
    dtlpassthruchar13: string;
    dtlpassthruchar14: string;
    dtlpassthruchar15: string;
    dtlpassthruchar16: string;
    dtlpassthruchar17: string;
    dtlpassthruchar18: string;
    dtlpassthruchar19: string;
    dtlpassthruchar20: string;
    dtlpassthruchar21: string;
    dtlpassthruchar22: string;
    dtlpassthruchar23: string;
    dtlpassthruchar24: string;
    dtlpassthruchar25: string;
    dtlpassthruchar26: string;
    dtlpassthruchar27: string;
    dtlpassthruchar28: string;
    dtlpassthruchar29: string;
    dtlpassthruchar30: string;
    dtlpassthruchar31: string;
    dtlpassthruchar32: string;
    dtlpassthruchar33: string;
    dtlpassthruchar34: string;
    dtlpassthruchar35: string;
    dtlpassthruchar36: string;
    dtlpassthruchar37: string;
    dtlpassthruchar38: string;
    dtlpassthruchar39: string;
    dtlpassthruchar40: string;
    dtlpassthrudate01: string;
    dtlpassthrudate02: string;
    dtlpassthrudate03: string;
    dtlpassthrudate04: string;
    dtlpassthrudoll01: number;
    dtlpassthrudoll02: number;
    dtlpassthrunum01: number;
    dtlpassthrunum02: number;
    dtlpassthrunum03: number;
    dtlpassthrunum04: number;
    dtlpassthrunum05: number;
    dtlpassthrunum06: number;
    dtlpassthrunum07: number;
    dtlpassthrunum08: number;
    dtlpassthrunum09: number;
    dtlpassthrunum10: number;
    dtlpassthrunum11: number;
    dtlpassthrunum12: number;
    dtlpassthrunum13: number;
    dtlpassthrunum14: number;
    dtlpassthrunum15: number;
    dtlpassthrunum16: number;
    dtlpassthrunum17: number;
    dtlpassthrunum18: number;
    dtlpassthrunum19: number;
    dtlpassthrunum20: number;
  }>()
);

export const AddItemDialogStep1ToStep0 = createAction('[Order.Add-Item-Dialog] - Step 1 to Step 0');

export const AddItemDialogStep2ToStep1 = createAction('[Order.Add-Item-Dialog] - Step 2 to Step 1');

export const AddItemDialogSuccess = createAction('[Order.Add-Item-Dialog] - Success', props<{ response: OrderAddItemResponseBody }>());
export const AddItemDialogFailure = createAction('[Order.Add-Item-Dialog] - Failure', props<{ error: HttpErrorResponse }>());

//
// order / create
//

export const CreateOrderInit = createAction(
  '[Order.Create] - Init',
  props<{ payload: OrderCreateRequest; uniqueOrderIdentifier: string }>()
);

export const CreateOrderSetValues = createAction('[Order.Create] - Set Values', props<{ payload: OrderCreateRequest }>());
export const CreateOrderClearValues = createAction('[Order.Create] - Clear Values');

export const CreateOrderRequest = createAction('[Order.Create] - Request', props<{ payload: OrderCreateRequest }>());
export const CreateOrderRequestSuccess = createAction(
  '[Order.Create] - Request Success',
  props<{ payload: OrderCreateRequest; data: OrderCreateResponseBody }>()
);
export const CreateOrderRequestFailure = createAction(
  '[Order.Create] - Request Failure',
  props<{ payload: OrderCreateRequest; error: HttpErrorResponse }>()
);

//
// order / duplicate reference and po check
//

export const OrderDuplicateCheckRequest = createAction(
  '[Order.Create] - Duplicate Check Request',
  props<{ payload: OrderCreateRequest; uniqueOrderIdentifier: string }>()
);
export const OrderDuplicateCheckRequestSuccess = createAction(
  '[Order.Create] - Duplicate Check Request Success',
  props<{ payload: OrderCreateRequest }>()
);
export const OrderDuplicateCheckRequestFailure = createAction(
  '[Order.Create] - Duplicate Check Request Failure',
  props<{ payload: OrderCreateRequest; error: HttpErrorResponse }>()
);

//
// order / update / duplicate reference and po check
//

export const OrderUpdateDuplicateCheckRequest = createAction(
  '[Order.Update] - Duplicate Check Request',
  props<{ payload: OrderInquiryResponseRow; index: number; uniqueOrderIdentifier: string }>()
);
export const OrderUpdateDuplicateCheckRequestSuccess = createAction(
  '[Order.Update] - Duplicate Check Request Success',
  props<{ payload: OrderInquiryResponseRow; index: number }>()
);
export const OrderUpdateDuplicateCheckRequestFailure = createAction(
  '[Order.Update] - Duplicate Check Request Failure',
  props<{ payload: OrderInquiryResponseRow; index: number; error: HttpErrorResponse }>()
);

//
// order / delete item
//

export const OrderDeleteItemRequest = createAction(
  '[Order] - Delete Item Request',
  props<{ orderid: number; shipid: number; item: string; lotNumber?: string }>()
);
export const OrderDeleteItemRequestSuccess = createAction(
  '[Order] - Delete Item Request Success',
  props<{ orderid: number; shipid: number; item: string; lotNumber?: string }>()
);
export const OrderDeleteItemRequestFailure = createAction('[Order] - Delete Item Request Failure', props<{ error: HttpErrorResponse }>());
